import React, { useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import { ArrowUpRight, Terminal, Check } from 'lucide-react';
import { COMMAND_ENTRIES, EMAIL } from '../mock';

/**
 * Command palette ("sirin_os") — ⌘K / Ctrl+K.
 * Dark forest/ink theme to contrast the warm editorial site below.
 * Commands deep-link into hero chips, work projects, and sections.
 */
export default function CommandPalette({ open, setOpen }) {
  const [copied, setCopied] = React.useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const scrollTo = (selector) => {
    const el = document.querySelector(selector);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSelect = (item) => {
    const { href } = item;

    // Copy email easter egg
    if (href === 'copy:email') {
      navigator.clipboard?.writeText(EMAIL).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      });
      setOpen(false);
      return;
    }

    // External / mailto
    if (href.startsWith('mailto:') || href.startsWith('http')) {
      window.open(href, href.startsWith('mailto:') ? '_self' : '_blank');
      setOpen(false);
      return;
    }

    // Deep-link: #hero:<chipIndex>
    if (href.startsWith('#hero:')) {
      const idx = parseInt(href.split(':')[1], 10);
      scrollTo('#top');
      window.dispatchEvent(
        new CustomEvent('sirin:hero-prompt', { detail: { index: idx } })
      );
      setOpen(false);
      return;
    }

    // Deep-link: #work:<projectId>
    if (href.startsWith('#work:')) {
      const id = href.split(':')[1];
      scrollTo('#work');
      window.dispatchEvent(
        new CustomEvent('sirin:work-focus', { detail: { id } })
      );
      setOpen(false);
      return;
    }

    // Regular in-page anchor
    if (href.startsWith('#')) {
      scrollTo(href);
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Title className="sr-only">
          Sirin OS — Command Palette
        </DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          Keyboard-driven explorer. Navigate sections, jump to projects, or reach out.
        </DialogPrimitive.Description>

        <div
          className="w-full"
          style={{
            background: 'var(--ink)',
            color: 'var(--cream)',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {/* Terminal bar */}
          <div
            className="flex items-center justify-between px-4 pt-3 pb-2 mono"
            style={{
              fontSize: 10,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#8a9a8d',
              borderBottom: '1px solid rgba(244,239,230,0.06)',
            }}
          >
            <span className="flex items-center gap-2">
              <Terminal size={11} strokeWidth={1.75} style={{ color: 'var(--terracotta)' }} />
              <span>sirin_os</span>
              <span style={{ color: 'rgba(244,239,230,0.2)' }}>·</span>
              <span>command</span>
            </span>
            <span>esc to close</span>
          </div>

          {/* Input */}
          <div
            className="px-1"
            style={{
              borderBottom: '1px solid rgba(244,239,230,0.06)',
            }}
          >
            <CommandInput
              placeholder="Ask Sirin anything. Try: 'email' or 'what do you do'"
              className="!h-14 text-[15px]"
              style={{
                color: 'var(--cream)',
                caretColor: 'var(--terracotta)',
              }}
            />
          </div>

          {/* List */}
          <CommandList
            className="max-h-[440px] px-1 py-1"
            style={{ background: 'transparent' }}
          >
            <CommandEmpty
              className="py-8 text-center mono"
              style={{ color: '#8a9a8d', fontSize: 12, letterSpacing: '0.04em' }}
            >
              No match. Try &lsquo;email&rsquo;, &lsquo;bkkbutter&rsquo;, or &lsquo;work&rsquo;.
            </CommandEmpty>

            {COMMAND_ENTRIES.map((group, gi) => (
              <CommandGroup
                key={group.group}
                heading={group.group}
                className="[&_[cmdk-group-heading]]:mono [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.22em] [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:py-3 [&_[cmdk-group-heading]]:text-[color:#8a9a8d]"
              >
                {group.items.map((item) => (
                  <CommandItem
                    key={`${gi}-${item.cmd}`}
                    value={`${item.cmd} ${item.hint}`}
                    onSelect={() => handleSelect(item)}
                    className="group !px-3 !py-3 rounded-md cursor-pointer
                               data-[selected=true]:!bg-[rgba(244,239,230,0.06)]
                               data-[selected=true]:!text-[color:var(--cream)]"
                    style={{ color: 'var(--cream)' }}
                  >
                    <span className="flex-1 text-[14px]" style={{ color: 'var(--cream)' }}>
                      {item.cmd}
                    </span>
                    <CommandShortcut
                      className="mono flex items-center gap-1.5"
                      style={{
                        color: '#8a9a8d',
                        fontSize: 10,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                      }}
                    >
                      <span>{item.hint}</span>
                      <ArrowUpRight size={11} strokeWidth={1.75} />
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}

            <CommandSeparator
              style={{ background: 'rgba(244,239,230,0.06)' }}
            />

            <div
              className="px-4 py-3 mono flex items-center justify-between"
              style={{
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#8a9a8d',
              }}
            >
              <span>
                ↑↓ navigate
                <span style={{ color: 'rgba(244,239,230,0.2)' }}> · </span>
                ↵ select
                <span style={{ color: 'rgba(244,239,230,0.2)' }}> · </span>
                esc close
              </span>
              <span>v.sirin_os_1.0</span>
            </div>
          </CommandList>
        </div>
      </CommandDialog>

      {/* Toast for copy-email */}
      {copied && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] mono inline-flex items-center gap-2 px-4 py-2.5 rounded-full"
          style={{
            background: 'var(--ink)',
            color: 'var(--cream)',
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            boxShadow: '0 12px 30px -10px rgba(15,27,20,0.4)',
          }}
        >
          <Check size={12} strokeWidth={2} style={{ color: '#84e1a6' }} />
          Email copied
        </div>
      )}
    </>
  );
}
